import { useId, useState } from 'react';
import { useRevalidator } from 'react-router';

import { Badge, Button, Card, Icon } from '@ds/core';
import { DataTable } from '@ds/data';
import type { DataTableColumn } from '@ds/data';
import { Modal } from '@ds/feedback';
import { FormField, Input, SearchField, Switch } from '@ds/forms';
import { centavosDoTexto, textoDosCentavos } from '@/comuns/dinheiro';
import { ExportMenu } from '@/comuns/ExportMenu';
import { moeda } from '@/comuns/formato';
import { useAviso } from '@/comuns/toast';
import { dados } from '@/data/porta';
import type { PacoteDoCatalogo } from '@/data/cadastros';

function normalizar(texto: string): string {
    return texto.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

export { centavosDoTexto };

/**
 * Pacotes — o catálogo do que a clínica vende.
 *
 * Não confundir com o pacote do paciente (Vendas › Pacotes): aquele é o que
 * alguém COMPROU, com sessões usadas e validade própria.
 */
export function PacotesDoCatalogoAba({ pacotes }: { pacotes: PacoteDoCatalogo[] }) {
    const revalidator = useRevalidator();
    const aviso = useAviso();
    const idNome = useId();
    const idSessoes = useId();
    const idPreco = useId();

    const [busca, setBusca] = useState('');
    const [alvo, setAlvo] = useState<PacoteDoCatalogo | 'novo' | null>(null);
    const [nome, setNome] = useState('');
    const [sessoes, setSessoes] = useState('');
    const [preco, setPreco] = useState('');
    const [ativo, setAtivo] = useState(true);
    const [salvando, setSalvando] = useState(false);

    const abrir = (p: PacoteDoCatalogo | 'novo') => {
        setAlvo(p);
        setNome(p === 'novo' ? '' : p.nome);
        setSessoes(p === 'novo' || p.sessoes === undefined ? '' : String(p.sessoes));
        setPreco(p === 'novo' ? '' : textoDosCentavos(p.precoCentavos));
        setAtivo(p === 'novo' ? true : p.ativo);
    };

    const precoCentavos = centavosDoTexto(preco);
    const sessoesNumero = sessoes.trim() === '' ? undefined : Number(sessoes);
    const sessoesValidas = sessoesNumero === undefined || (Number.isInteger(sessoesNumero) && sessoesNumero > 0);
    const sujo = alvo === 'novo'
        ? nome.trim() !== '' || preco.trim() !== '' || sessoes.trim() !== ''
        : alvo !== null && (nome !== alvo.nome || precoCentavos !== alvo.precoCentavos || sessoesNumero !== alvo.sessoes || ativo !== alvo.ativo);

    const salvar = async () => {
        if (!alvo || precoCentavos === null || !sessoesValidas) return;
        setSalvando(true);
        try {
            const existente = alvo === 'novo' ? undefined : alvo;
            await dados().salvarPacoteDoCatalogo(existente?.id ?? null, {
                nome, precoCentavos, ativo,
                ...(sessoesNumero === undefined ? {} : { sessoes: sessoesNumero }),
                ...(existente?.procedimentoId ? { procedimentoId: existente.procedimentoId } : {}),
                ...(existente?.validadeDias === undefined ? {} : { validadeDias: existente.validadeDias }),
            });
            aviso({ title: existente ? 'Pacote atualizado' : 'Pacote criado', description: nome.trim() });
            setAlvo(null);
            await revalidator.revalidate();
        } catch (e) {
            aviso({ title: 'Não foi possível salvar o pacote', description: e instanceof Error ? e.message : 'Tente de novo.', tone: 'danger' });
        } finally {
            setSalvando(false);
        }
    };

    const visiveis = pacotes.filter((p) => normalizar(p.nome).includes(normalizar(busca.trim())));

    const colunas: DataTableColumn<PacoteDoCatalogo>[] = [
        { key: 'nome', header: 'Nome', render: (p) => <strong style={{ fontWeight: 600 }}>{p.nome}</strong> },
        { key: 'sessoes', header: 'Sessões', align: 'right', width: 100, render: (p) => (p.sessoes === undefined ? '—' : p.sessoes) },
        { key: 'precoCentavos', header: 'Preço', align: 'right', width: 140, render: (p) => <span style={{ fontVariantNumeric: 'tabular-nums' }}>{moeda(p.precoCentavos)}</span> },
        { key: 'ativo', header: 'Situação', width: 120, render: (p) => <Badge tone={p.ativo ? 'success' : 'neutral'}>{p.ativo ? 'Ativo' : 'Desativado'}</Badge> },
        { key: 'acoes', header: '', width: 100, align: 'right', render: (p) => <Button size="sm" variant="secondary" onClick={() => abrir(p)}>Editar</Button> },
    ];

    return (
        <>
            <Card
                title="Pacotes" padding={0}
                subtitle="O que a clínica vende. O pacote que o paciente comprou fica em Vendas."
                actions={(
                    <span style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        <ExportMenu
                            payload={{
                                title: 'Pacotes',
                                columns: [
                                    { header: 'Nome', value: (p: PacoteDoCatalogo) => p.nome },
                                    { header: 'Sessões', value: (p: PacoteDoCatalogo) => p.sessoes ?? '' },
                                    { header: 'Preço', value: (p: PacoteDoCatalogo) => moeda(p.precoCentavos) },
                                    { header: 'Situação', value: (p: PacoteDoCatalogo) => (p.ativo ? 'Ativo' : 'Desativado') },
                                ],
                                rows: visiveis,
                            }}
                        />
                        <Button size="sm" iconLeft={<Icon name="plus" size={14} />} onClick={() => abrir('novo')}>Novo pacote</Button>
                    </span>
                )}
            >
                <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border-subtle)' }}>
                    <SearchField value={busca} onChange={setBusca} placeholder="Buscar pacote" ariaLabel="Buscar pacote" />
                </div>
                <DataTable
                    columns={colunas} rows={visiveis} rowKey="id" stickyHeader={false} style={{ border: 0, borderRadius: 0 }}
                    emptyMessage={busca ? `Nenhum pacote com “${busca}”.` : 'Nenhum pacote ainda.'}
                />
            </Card>

            <Modal
                open={alvo !== null} width={480} title={alvo === 'novo' ? 'Novo pacote' : 'Editar pacote'}
                onClose={() => setAlvo(null)} dirty={sujo} onSave={() => { void salvar(); }}
                footer={(
                    <>
                        <Button variant="ghost" onClick={() => setAlvo(null)}>Cancelar</Button>
                        <Button onClick={() => { void salvar(); }} disabled={!nome.trim() || precoCentavos === null || !sessoesValidas || salvando}>
                            {salvando ? 'Salvando…' : 'Salvar'}
                        </Button>
                    </>
                )}
            >
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 14 }}>
                    <FormField label="Nome" htmlFor={idNome} required style={{ gridColumn: '1 / -1' }}>
                        <Input id={idNome} value={nome} onChange={setNome} placeholder="Pacote 10 sessões — Limpeza de pele" />
                    </FormField>
                    <FormField label="Sessões" htmlFor={idSessoes} hint="Em branco para pacote por período" error={sessoesValidas ? undefined : 'Número inteiro maior que zero'}>
                        <Input id={idSessoes} type="number" min={1} inputMode="numeric" value={sessoes} onChange={setSessoes} invalid={!sessoesValidas} />
                    </FormField>
                    <FormField label="Preço" htmlFor={idPreco} required error={preco.trim() && precoCentavos === null ? 'Valor inválido' : undefined}>
                        <Input
                            id={idPreco} value={preco} onChange={setPreco} inputMode="decimal" placeholder="2.240,00"
                            iconLeft={<span style={{ fontSize: 13 }}>R$</span>} invalid={Boolean(preco.trim()) && precoCentavos === null}
                        />
                    </FormField>
                    <Switch checked={ativo} onChange={setAtivo} label="Ativo" style={{ gridColumn: '1 / -1' }} />
                </div>
            </Modal>
        </>
    );
}
